import { styled } from '@linaria/react';
import { Group, Select } from '@mantine/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { AgentAdoptionResponse } from '../../../api/agent-miner-client/agent-miner-client.type';
import { inkLight, secondaryBase, skyLight } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import { H3, Text } from '../../../ui-library/typography/typography';
import { LineChartTooltip } from '../../process-analysis/components/line-chart/line-chart-tooltip';
import { ADOPTION_SERIES_KEY, buildAdoptionTrendData } from './adoption.helpers';
import { useAdoptionTrendMeasure } from './adoption.hooks';
import type { AdoptionTrendMeasure } from './adoption.type';

type Props = {
  response: AgentAdoptionResponse;
};

const TREND_MEASURES: AdoptionTrendMeasure[] = ['active_users', 'sessions', 'adoption_rate'];

/** Material icon shown beside each trend measure in the chart's measure select. */
const MEASURE_ICONS: Record<AdoptionTrendMeasure, string> = {
  active_users: 'group',
  sessions: 'forum',
  adoption_rate: 'trending_up',
};

/**
 * The selected adoption series (active users, sessions, or adoption rate)
 * per period as a single line, with the measure selector in the chart's own
 * header (URL-backed via `ad_measure`, like the Delivery Impact chart's
 * line/bar toggle). Future periods emit null and are NOT bridged — the gap
 * distinguishes "no data yet" from a real zero. The adoption-rate measure
 * pins the axis to 0–100 so the rate reads against its full scale.
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} The chart card content
 */
export const AdoptionTrendChart = ({ response }: Props) => {
  const { t } = useTranslation();
  const { measure, setMeasure } = useAdoptionTrendMeasure();
  const chartData = useMemo(() => buildAdoptionTrendData(response, measure), [response, measure]);
  const isRate = measure === 'adoption_rate';
  const measureLabel = t(`agent_miner.adoption.measure_${measure}`);

  const formatValue = (value: number | null) =>
    value !== null && value !== undefined ? `${value}${isRate ? '%' : ''}` : '—';

  return (
    <ChartLayout data-testid="adoption-trend-chart">
      <Header>
        <TitleBlock>
          <H3>{t('agent_miner.adoption.trend_title', { measure: measureLabel })}</H3>
          <Text size="small" color={inkLight}>
            {t('agent_miner.adoption.trend_subtitle')}
          </Text>
        </TitleBlock>
        <Select
          aria-label={t('agent_miner.adoption.measure_label')}
          data-testid="adoption-measure-select"
          data={TREND_MEASURES.map((trendMeasure) => ({
            value: trendMeasure,
            label: t(`agent_miner.adoption.measure_${trendMeasure}`),
          }))}
          value={measure}
          onChange={(next) => {
            if (next) setMeasure(next as AdoptionTrendMeasure);
          }}
          leftSection={<Icon name={MEASURE_ICONS[measure]} size={16} color={inkLight} />}
          renderOption={({ option }) => (
            <Group gap={8} wrap="nowrap">
              <Icon name={MEASURE_ICONS[option.value as AdoptionTrendMeasure]} size={16} color={inkLight} />
              {option.label}
            </Group>
          )}
          size="sm"
          radius="xl"
          allowDeselect={false}
          w={190}
        />
      </Header>
      <ChartArea>
        <ResponsiveContainer width="100%" height="100%" minHeight={160}>
          <ComposedChart data={chartData} margin={{ top: 20, right: 10, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={skyLight} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12 }}
              axisLine={{ stroke: skyLight }}
              tickLine={{ stroke: skyLight }}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              axisLine={{ stroke: skyLight }}
              tickLine={{ stroke: skyLight }}
              domain={isRate ? [0, 100] : undefined}
              label={{
                value: measureLabel,
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle' },
                offset: 10,
              }}
            />
            <RechartsTooltip content={<LineChartTooltip formatValue={formatValue} />} />
            <Line
              type="monotone"
              dataKey={ADOPTION_SERIES_KEY}
              name={measureLabel}
              stroke={secondaryBase}
              strokeWidth={2.5}
              isAnimationActive={false}
              connectNulls={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartArea>
    </ChartLayout>
  );
};

const ChartLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 420px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

/* The chart area must own the remaining vertical space or the chart collapses
   to the ResponsiveContainer's 160px floor. */
const ChartArea = styled.div`
  flex: 1;
  min-height: 0;
`;
